import { PayGroup } from '../../../shared/models/pay-group.model';
import { Moment } from 'moment';
import * as moment from 'moment-timezone';
import { CustomerNotification } from '../../../services/customer-notification.model';
import { CustomerNotificationType, AckStatus } from '../../../shared/enums/smarthub-enums';
import { PayrollCycleType } from '../../../services/payroll-types.service';

enum FundType {
  All = 0,
  Returned = 1,
  NotReturned = 2
}

export class AcknowledgmentFilter {
  static readonly minDate: Moment = moment({ year: 2018, month: 8, day: 1 });

  includeAck: AckStatus;
  fromAckDateTime: Moment;
  toAckDateTime: Moment;

  isFromDateInvalid = false;
  isToDateInvalid = false;

  constructor(init?: Partial<AcknowledgmentFilter>) {
      Object.assign(this, init);
  }

  static get default(): AcknowledgmentFilter {
    const toAckDate = moment.tz('America/New_York');
    const fromAckDate = toAckDate.clone().utc().subtract(15, 'days').tz('America/New_York');

    return new AcknowledgmentFilter({
      includeAck: AckStatus.All,
      fromAckDateTime: fromAckDate,
      toAckDateTime: toAckDate
    });
  }

  get hasAcknowledged(): boolean {
    return [AckStatus.Ack, AckStatus.All].includes(this.includeAck);
  }
  set hasAcknowledged(val: boolean) {
    this.includeAck = this.hasUnAcknowledged
      ? val ? AckStatus.All : AckStatus.UnAck
      : val ? AckStatus.Ack : undefined;
  }

  get hasUnAcknowledged(): boolean {
    return [AckStatus.UnAck, AckStatus.All].includes(this.includeAck);
  }
  set hasUnAcknowledged(val: boolean) {
    this.includeAck = this.hasAcknowledged
      ? val ? AckStatus.All : AckStatus.Ack
      : val ? AckStatus.UnAck : undefined;
  }

  get fromPrecedesTo(): boolean {
    return this.fromAckDateTime <= this.toAckDateTime;
  }

  get isValid(): boolean {
    return !this.isFromDateInvalid
      && !this.isToDateInvalid
      && this.fromAckDateTime && this.toAckDateTime
      && this.fromPrecedesTo
      && [this.fromAckDateTime, this.toAckDateTime].every(d => moment(d) >= AcknowledgmentFilter.minDate);
  }

  resetDates(): void {
    const defaultFilter = AcknowledgmentFilter.default;
    this.fromAckDateTime = defaultFilter.fromAckDateTime;
    this.toAckDateTime = defaultFilter.toAckDateTime;
    this.isFromDateInvalid = false;
    this.isToDateInvalid = false;
  }
}

export class AlertsFilters {
  static get default(): AlertsFilters {
    return new AlertsFilters({
      ackFilter: AcknowledgmentFilter.default,
      payDate: null,
      payGroups: [],
      fundType: FundType.All,
      payrollType: PayrollCycleType.All,
      componentCompany: 'all'
    });
  }

  ackFilter: AcknowledgmentFilter;
  payDate: Moment;
  payGroups: PayGroup[];
  fundType: FundType;
  payrollType: PayrollCycleType;
  componentCompany: string;
  isPayDateInvalid = false;
  readonly minDate: moment.Moment = AcknowledgmentFilter.minDate;


  constructor(init?: Partial<AlertsFilters>) {
    Object.assign(this, init);
  }

  get customerNotificationTypes(): CustomerNotificationType[] {
    const types: CustomerNotificationType[] = [];
    if (this.hasFundTypeReturned) {
      types.push.apply(types, CustomerNotification.typesWithReturnedFunds);
    }
    if (this.hasFundTypeNotReturned) {
      types.push.apply(types, CustomerNotification.typesWithoutReturnedFunds);
    }
    return types;
  }

  get hasPayGroups(): boolean {
    return !!this.payGroups && this.payGroups.length > 0;
  }

  get hasFundTypes(): boolean {
    return !!this.fundType;
  }

  get hasComponentCompany(): boolean {
    return !['', 'all', null, undefined].includes(this.componentCompany);
  }

  payrollTypeCompareWith(currentValue, compareValue): boolean {
    return currentValue.toString() === compareValue.toString();
  }

  get hasFundTypeReturned(): boolean {
    return [FundType.Returned, FundType.All].includes(this.fundType);
  }
  set hasFundTypeReturned(val: boolean) {
    this.fundType = this.hasFundTypeNotReturned
      ? val ? FundType.All : FundType.NotReturned
      : val ? FundType.Returned : undefined;
  }

  get hasFundTypeNotReturned(): boolean {
    return [FundType.NotReturned, FundType.All].includes(this.fundType);
  }
  set hasFundTypeNotReturned(val: boolean) {
    this.fundType = this.hasFundTypeReturned
      ? val ? FundType.All : FundType.Returned
      : val ? FundType.NotReturned : undefined;
  }

  get hasAcknowledged(): boolean {
    return this.ackFilter.hasAcknowledged;
  }
  set hasAcknowledged(val: boolean) {
    this.ackFilter.hasAcknowledged = val;
  }

  get hasUnAcknowledged(): boolean {
    return this.ackFilter.hasUnAcknowledged;
  }
  set hasUnAcknowledged(val: boolean) {
    this.ackFilter.hasUnAcknowledged = val;
  }

  get isValid(): boolean {
    return !this.isPayDateInvalid && this.ackFilter.isValid;
  }
}


